import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DCWMPStep2DevelopmentScope = ({ project, onComplete, onBack }) => {
  const [formData, setFormData] = useState({
    construction_components: [{ description: '', gfa: '' }]
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (project?.dcwmp_step2?.construction_components?.length > 0) {
      setFormData({
        construction_components: project.dcwmp_step2.construction_components.map(c => ({
          description: c.description || '',
          gfa: c.gfa || ''
        }))
      });
    }
  }, [project]);

  const addComponent = () => {
    setFormData(prev => ({
      ...prev,
      construction_components: [...prev.construction_components, { description: '', gfa: '' }]
    }));
  };

  const removeComponent = (index) => {
    if (formData.construction_components.length > 1) {
      setFormData(prev => ({
        ...prev,
        construction_components: prev.construction_components.filter((_, i) => i !== index)
      }));
    }
  };

  const updateComponent = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      construction_components: prev.construction_components.map((comp, i) => 
        i === index ? { ...comp, [field]: value } : comp
      )
    }));
  };

  const getTotalGFA = () => {
    return formData.construction_components.reduce((sum, comp) => {
      return sum + (parseFloat(comp.gfa) || 0);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('opwmp_token');
      
      // Filter out empty components and convert GFA to numbers
      const validComponents = formData.construction_components
        .filter(c => c.description && c.gfa)
        .map(c => ({
          description: c.description,
          gfa: parseFloat(c.gfa)
        }));

      if (validComponents.length === 0) {
        setError('Please add at least one construction component');
        setLoading(false);
        return;
      }

      await axios.put(
        `${API}/api/projects/${project.id}/dcwmp/step2`,
        { construction_components: validComponents },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-white/30 rounded-md text-sm font-medium text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-white">Development Scope</h2>
        <p className="text-blue-100 mt-1">Define the construction components and their gross floor areas</p>
      </div>

      <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="h-5 w-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-blue-700">
              List all construction components that will be built. The GFA (Gross Floor Area) is used to calculate estimated construction waste volumes.
            </p>
          </div>
        </div>

        {/* Construction Components */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Construction Components <span className="text-red-500">*</span>
          </label>
          
          <div className="space-y-3">
            {formData.construction_components.map((comp, index) => (
              <div key={index} className="flex gap-3 items-start">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Component description (e.g., Retail Tenancies)"
                    value={comp.description}
                    onChange={(e) => updateComponent(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="w-32">
                  <input
                    type="number"
                    placeholder="GFA (m²)"
                    value={comp.gfa}
                    onChange={(e) => updateComponent(index, 'gfa', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                  />
                </div>
                {formData.construction_components.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeComponent(index)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addComponent}
            className="mt-3 inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Component
          </button>
        </div>

        {/* Total GFA Display */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Total Construction GFA:</span>
            <span className="text-xl font-bold text-blue-600">{getTotalGFA().toLocaleString()} m²</span>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DCWMPStep2DevelopmentScope;
